import { Component, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';

import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { BookJob } from 'src/providers/book-job/book-job';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';
import { Loading } from 'src/shared/components/loading/loading';
import { OnboardingComponent } from 'src/shared/components/onboarding/onboarding';

@Component({
    selector: 'service-rename-onboarding',
    templateUrl: './service-rename-onboarding.html',
    standalone: false
})
export class ServiceRenameOnboardingComponent implements OnInit {

  form: UntypedFormGroup;
  services: any[];
  loaded: boolean;
  errorMessage: string;

  boundSave = this.save.bind(this);
  boundUpdateServiceName = this.updateServiceName.bind(this);

constructor(
    public onboardingProvider: OnboardingProvider,
    private navCtrl: CustomNavController,
    private util: Util,
    private bookJob: BookJob,
    private fb: UntypedFormBuilder,
    private rightSidePanelService: RightSidePanelService,
    public windowService: WindowService
  ) {
    this.form = this.fb.group({});
  }

  async ngOnInit() {
    this.loaded = false;
    await this.loadServices();
    this.loaded = true;
  }

  @Loading('', true)
  async loadServices() {
    try {
      const services = await this.bookJob.fetchAllRequiredTeamServices();
      this.services = services.map(service => {
        const originalName = service.original_name || service.name;
        const currentName = service.name || service.original_name;
        const hasCustomName = currentName !== originalName;

        return {
          id: service.id,
          key: service.key,
          originalName,
          customName: hasCustomName ? currentName : '',
          name: currentName
        };
      });

      // Create form controls for each service
      const formControls = {};
      this.services.forEach(service => {
        formControls[`service_${service.id}`] = [service.customName || ''];
      });
      this.form = this.fb.group(formControls);
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  getUpdateServiceNameAction(serviceId: number) {
    return () => this.updateServiceName(serviceId);
  }

  async updateServiceName(serviceId: number) {
    try {
      const service = this.services.find(s => s.id === serviceId);
      if (!service) {
        this.util.showError('Service not found.');
        return;
      }

      const controlName = `service_${service.id}`;
      const customName = this.form.get(controlName)?.value?.trim() || '';

      if (customName === service.originalName) {
        await this.bookJob.updateRequiredTeamServiceName(service.id, '');
        service.customName = '';
        service.name = service.originalName;
      } else if (customName) {
        await this.bookJob.updateRequiredTeamServiceName(service.id, customName);
        service.customName = customName;
        service.name = customName;
      } else {
        await this.bookJob.updateRequiredTeamServiceName(service.id, '');
        service.customName = '';
        service.name = service.originalName;
      }
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      this.util.showError(this.errorMessage);
    }
  }

  async save() {
    try {
      // Mark as completed if at least one service has been customized or user has interacted
      // For now, we'll mark it as done if they've visited this page
      await this.onboardingProvider.markGoalAsCompleted('customizeServiceNames');
      this.rightSidePanelService.setDialogPageTitle('Getting Started');
      this.rightSidePanelService.navigateTo('onboarding', {}, OnboardingComponent);
      this.onboardingProvider.setShowOnboardingInDashboard(true);
      this.onboardingProvider.setShowOnboardingInHeader(true);
      this.util.showSuccess('Service names setup complete');
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  skip() {
    this.save();
  }
}

